import { useEffect, useMemo, useState, type ReactElement } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "motion/react";
import {
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Clock,
  Plus,
  Check,
  Package,
  BookOpen,
  FileText,
  Target,
  Lightbulb,
  Lock,
  Search,
  Settings,
  Megaphone,
  Zap,
  DollarSign,
  Rocket,
  X,
  type LucideIcon,
} from "lucide-react";
import { useCompanyId } from "@/lib/CompanyContext";
import { templatesApi, type IssueTemplate, type TemplateField, type ArtBriefing } from "@/api/templates";
import ArtBriefingQuestions from "@/components/ArtBriefingQuestions";
import { TaskArtImageInputs } from "@/components/TaskArtImageInputs";
import { projectsApi } from "@/api/projects";
import { knowledgeApi, type KnowledgeBase, type KnowledgeBaseType } from "@/api/knowledge";
import { integrationsApi, type CompanyIntegration } from "@/api/integrations";
import { productLibraryApi } from "@/api/product-library";
import { assetsApi } from "@/api/assets";
import { ReferenceImagePickerModal } from "@/components/ReferenceImagePickerModal";
import { queryKeys } from "@/lib/queryKeys";
import { getTemplateMeta, getTemplateFlow, PHASE_COLORS, AGENTS, type AgentKey } from "@/lib/templateMeta";
import { Notice, InfoTooltip, RequiredMark } from "@/components/ui";

/**
 * Letras ESCALA — metodologia ND aplicada a /nova-tarefa.
 *
 * Cada template é classificado numa das 6 letras pelo backend (campo
 * issue_templates.phase). UI agrupa templates por letra na ordem natural
 * do método: Entender → Sistematizar → Captar → Ativar → Lucrar → Acelerar.
 *
 * Cores definidas em /lib/templateMeta.PHASE_COLORS (degradê laranja → azul ND).
 */
const PHASE_ORDER: Array<{
  key: "E" | "S" | "C" | "A" | "L" | "A2";
  label: string;
  number: string;
  icon: LucideIcon;
  description: string;
  agents: string;
}> = [
  {
    key: "E",
    number: "E",
    icon: Search,
    label: "Entender",
    description: "Pesquisa, benchmark, dados — antes de qualquer execução, entenda mercado e cliente.",
    agents: "Analista de Mercado · Estrategista",
  },
  {
    key: "S",
    number: "S",
    icon: Settings,
    label: "Sistematizar",
    description: "Frameworks e processos — big idea, esteira, calendário, automações de fluxo.",
    agents: "Estrategista · Copywriter",
  },
  {
    key: "C",
    number: "C",
    icon: Megaphone,
    label: "Captar",
    description: "Conteúdo orgânico que atrai e nutre — posts, stories, reels, e-mails de relacionamento.",
    agents: "Social Media · Copywriter",
  },
  {
    key: "A",
    number: "A",
    icon: Zap,
    label: "Ativar",
    description: "Conversão e oferta direta — VSL, landing, sales letter, mockup de produto.",
    agents: "Copywriter",
  },
  {
    key: "L",
    number: "L",
    icon: DollarSign,
    label: "Lucrar",
    description: "Otimização e mensuração — relatórios, A/B test, recuperação, retenção.",
    agents: "Estrategista · Tráfego",
  },
  {
    key: "A2",
    number: "A²",
    icon: Rocket,
    label: "Acelerar",
    description: "Tráfego pago e escala — anúncios Meta/Google, vídeo IA, voz IA pra ads.",
    agents: "Tráfego · Diretor de Arte",
  },
];

// Templates _arte são sub-tarefas criadas automaticamente após aprovação da copy.
// O usuário não escolhe arte direto — só copy.
// Templates kind=routine vão pra /automacoes, não /nova-tarefa.
function isUserSelectableTemplate(template: IssueTemplate): boolean {
  if (template.key.endsWith("_arte")) return false;
  if (template.kind === "routine") return false;
  return true;
}

// ─────────────────────────────────────────────────────────────
// Busca de templates — match por palavras + sinônimos (ex: "tráfego pago"
// expande pra "meta", "google", "ads", "anúncio"). Tokens AND, normaliza
// acentos. Pra cada token: ou aparece direto no haystack, ou via sinônimo.
// ─────────────────────────────────────────────────────────────
function normalizeForSearch(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

const TEMPLATE_SEARCH_SYNONYMS: Record<string, string[]> = {
  trafego: ["meta", "google", "ads", "anuncio", "anuncios", "campanha", "pago"],
  pago: ["meta", "google", "ads", "anuncio"],
  "trafego pago": ["meta", "google", "ads", "anuncio", "campanha"],
  ads: ["meta", "google", "anuncio", "trafego"],
  anuncio: ["meta", "google", "ads", "trafego"],
  campanha: ["meta", "google", "ads", "trafego"],
  criativo: ["arte", "carrossel", "post", "reel", "story"],
  arte: ["criativo", "imagem", "carrossel", "post"],
  post: ["instagram", "carrossel", "criativo", "social"],
  reel: ["video", "instagram"],
  story: ["instagram", "stories"],
  social: ["instagram", "post", "carrossel"],
  email: ["e-mail", "newsletter", "fluxo", "ativacao"],
  "e-mail": ["email", "newsletter", "fluxo"],
  newsletter: ["email", "fluxo"],
  vsl: ["roteiro", "video", "vendas"],
  copy: ["texto", "escrita"],
  lp: ["landing", "pagina"],
  landing: ["lp", "pagina"],
  concorrente: ["pesquisa", "benchmark", "competidor"],
  benchmark: ["concorrente", "pesquisa"],
  persona: ["publico", "icp"],
  publico: ["persona", "icp"],
};

function filterTemplatesByQuery(
  templates: IssueTemplate[],
  query: string,
): IssueTemplate[] {
  const q = normalizeForSearch(query.trim());
  if (!q) return templates;

  return templates.filter((t) => {
    const haystack = normalizeForSearch(
      [t.title, t.description, t.category, t.key].filter(Boolean).join(" "),
    );
    // Match direto da query inteira
    if (haystack.includes(q)) return true;
    // Match por sinônimo de frase inteira
    const phraseSyns = TEMPLATE_SEARCH_SYNONYMS[q];
    if (phraseSyns?.some((s) => haystack.includes(normalizeForSearch(s)))) {
      return true;
    }
    // Match por todos os tokens (AND): cada token precisa aparecer no haystack
    // diretamente ou via sinônimo.
    const tokens = q.split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return false;
    return tokens.every((tok) => {
      if (haystack.includes(tok)) return true;
      const syns = TEMPLATE_SEARCH_SYNONYMS[tok] ?? [];
      return syns.some((s) => haystack.includes(normalizeForSearch(s)));
    });
  });
}

// ─────────────────────────────────────────────────────────────
// Step 1 — Gallery
// ─────────────────────────────────────────────────────────────

function TemplateCard({
  template,
  onClick,
  delay = 0,
  lockedIntegration,
  phaseColor,
}: {
  template: IssueTemplate;
  onClick: () => void;
  delay?: number;
  /** Quando setado: card vira read-only com cadeado e link pra /integracoes. */
  lockedIntegration?: { key: string; name: string } | null;
  /** Cor da fase do funil — aplicada no ícone e hover do card. */
  phaseColor?: { primary: string; soft: string };
}) {
  const meta = getTemplateMeta(template.key);
  const Icon = meta?.icon ?? Sparkles;
  const isComingSoon = template.comingSoon === "true";
  const isLocked = !!lockedIntegration || isComingSoon;
  const accent = phaseColor?.primary ?? "var(--nd-orange)";
  const accentSoft = phaseColor?.soft ?? "rgba(255,112,0,0.10)";

  return (
    <motion.button
      type="button"
      onClick={isLocked ? undefined : onClick}
      whileHover={isLocked ? undefined : { y: -3 }}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: isLocked ? 0.65 : 1, y: 0 }}
      transition={{ duration: 0.35, delay, ease: [0.16, 1, 0.3, 1] }}
      style={{
        position: "relative",
        textAlign: "left",
        cursor: isLocked ? "default" : "pointer",
        padding: 18,
        background: isLocked ? "var(--wf-bg)" : "var(--wf-paper)",
        border: "1px solid var(--wf-ink-5)",
        borderRadius: 12,
        boxShadow: "0 1px 0 rgba(0,0,0,0.03)",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        font: "inherit",
        color: "inherit",
        minHeight: 168,
        transition: "border-color 150ms, box-shadow 150ms",
      }}
      onMouseEnter={(e) => {
        if (isLocked) return;
        e.currentTarget.style.borderColor = accent;
        e.currentTarget.style.boxShadow = `0 4px 16px ${accentSoft}`;
      }}
      onMouseLeave={(e) => {
        if (isLocked) return;
        e.currentTarget.style.borderColor = "var(--wf-ink-5)";
        e.currentTarget.style.boxShadow = "0 1px 0 rgba(0,0,0,0.03)";
      }}
    >
      {/* Header — type icon + ETA */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            position: "relative",
            width: 36,
            height: 36,
            borderRadius: 10,
            background: accentSoft,
            color: accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon size={17} strokeWidth={1.75} />
          {/* Selo da fonte de dado (ex: Google, Meta) — reusa o logo da
              integração. Comunica de onde vem o dado daquele card. */}
          {meta?.source && (
            <img
              src={`/integrations/${meta.source}.png`}
              alt=""
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
              style={{
                position: "absolute",
                right: -4,
                bottom: -4,
                width: 16,
                height: 16,
                borderRadius: 4,
                background: "#fff",
                padding: 1,
                boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
                objectFit: "contain",
              }}
            />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "var(--wf-ink-3)",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            {template.category}
          </div>
        </div>
        {meta?.eta && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
              fontSize: 10,
              fontWeight: 500,
              color: "var(--wf-ink-3)",
            }}
          >
            <Clock size={10} strokeWidth={1.75} />
            {meta.eta}
          </span>
        )}
      </div>

      {/* Title */}
      <h3
        className="wf-display"
        style={{
          fontSize: 18,
          fontWeight: 700,
          letterSpacing: "-0.015em",
          lineHeight: 1.2,
          color: "var(--wf-ink)",
          margin: 0,
        }}
      >
        {template.title}
      </h3>

      {/* Description — versão amigável */}
      <p
        style={{
          fontSize: 12,
          color: "var(--wf-ink-2)",
          lineHeight: 1.5,
          margin: 0,
          flex: 1,
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {meta?.userDescription ?? template.description ?? ""}
      </p>

      {/* Fluxo: indica se essa task vai disparar uma sub-task de arte
          depois da aprovação. Comunica o processo completo pro user. */}
      {!isLocked && template.nextTemplateKey && (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "4px 8px",
            background: accentSoft,
            color: accent,
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.02em",
            textTransform: "uppercase",
            alignSelf: "flex-start",
          }}
        >
          copy → arte
        </div>
      )}

      {/* Footer */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 6,
          paddingTop: 10,
          borderTop: "1px dashed var(--wf-ink-5)",
        }}
      >
        {!isLocked && meta?.tagline && (
          <span
            style={{
              fontSize: 11,
              color: "var(--wf-ink-3)",
              fontStyle: "italic",
            }}
          >
            {meta.tagline}
          </span>
        )}
        {isComingSoon ? (
          <span
            style={{
              marginLeft: "auto",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              color: "var(--wf-ink-2)",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "3px 8px",
              borderRadius: 999,
              background: "var(--wf-ink-6, #f3efe5)",
              border: "1px solid var(--wf-ink-5)",
            }}
          >
            <Lock size={10} strokeWidth={2} />
            em breve
          </span>
        ) : lockedIntegration ? (
          <Link
            to="/integracoes"
            onClick={(e) => e.stopPropagation()}
            style={{
              marginLeft: "auto",
              fontSize: 11,
              color: "var(--wf-ink-2)",
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              textDecoration: "none",
            }}
          >
            <Lock size={11} strokeWidth={2} />
            Conectar {lockedIntegration.name}
          </Link>
        ) : (
          <span
            style={{
              marginLeft: "auto",
              fontSize: 12,
              color: accent,
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            escolher <ArrowRight size={11} strokeWidth={2} />
          </span>
        )}
      </div>
    </motion.button>
  );
}

function GalleryStep({
  templates,
  onSelect,
  integrations,
}: {
  templates: IssueTemplate[];
  onSelect: (t: IssueTemplate) => void;
  integrations: CompanyIntegration[];
}) {
  const integrationByKey = useMemo(
    () => new Map(integrations.map((i) => [i.key, i])),
    [integrations],
  );

  // Busca inteligente — usa sinônimos pra mapear termos do user em palavras
  // que aparecem nos templates (ex: "tráfego pago" → encontra "Meta Ads",
  // "Google Ads"). Match é AND de tokens, com expansão por sinônimo. Acentos
  // são normalizados pra comparação.
  const [searchQuery, setSearchQuery] = useState("");
  const filteredTemplates = useMemo(
    () => filterTemplatesByQuery(templates, searchQuery),
    [templates, searchQuery],
  );

  const resolveLock = (t: IssueTemplate) => {
    if (!t.requiresIntegrationKey) return null;
    const integ = integrationByKey.get(t.requiresIntegrationKey);
    if (integ?.connection.connected) return null;
    return { key: t.requiresIntegrationKey, name: integ?.name ?? t.requiresIntegrationKey };
  };

  // Tratamento "executável vs bloqueado":
  //  - executável: comingSoon=false E (sem integration requirement OU integração conectada)
  //  - bloqueado: comingSoon=true OU integração necessária não conectada
  // No agrupamento por fase, executáveis vêm primeiro (alfabético) e
  // bloqueados depois — visualmente no fim e com cadeado.
  const isLockedByMissingIntegration = (t: IssueTemplate) =>
    !!resolveLock(t);
  const isBlockedTemplate = (t: IssueTemplate) =>
    t.comingSoon === "true" || isLockedByMissingIntegration(t);

  // Por padrão, mostra só os primeiros 5 cards bloqueados de cada fase.
  // User clica "Ver mais X em breve" pra expandir. Reduz poluição visual
  // — fases tipo C (Captar) tem 17 em breve que não cabem na primeira tela.
  const COMING_SOON_VISIBLE_LIMIT = 5;
  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(new Set());
  const togglePhaseExpanded = (key: string) =>
    setExpandedPhases((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Agrupa por phase (E/S/C/A/L/A2). Usa a lista FILTRADA pela busca.
  const grouped = useMemo(() => {
    const acc: Record<string, IssueTemplate[]> = {};
    for (const t of filteredTemplates) {
      const phase = t.phase ?? "copy";
      if (!acc[phase]) acc[phase] = [];
      acc[phase].push(t);
    }
    // Sort: executáveis primeiro (alfabético), bloqueados depois (alfabético).
    for (const phase of Object.keys(acc)) {
      acc[phase].sort((a, b) => {
        const aBlocked = isBlockedTemplate(a) ? 1 : 0;
        const bBlocked = isBlockedTemplate(b) ? 1 : 0;
        if (aBlocked !== bBlocked) return aBlocked - bBlocked;
        return a.title.localeCompare(b.title);
      });
    }
    return acc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTemplates, integrationByKey]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      {/* Header */}
      <header
        style={{
          padding: "32px 32px 20px",
          borderBottom: "1px solid var(--wf-ink-5)",
          background: "var(--wf-paper)",
        }}
      >
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 6,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                background: "rgba(255,112,0,0.12)",
                color: "var(--nd-orange)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Plus size={16} strokeWidth={1.75} />
            </div>
            <h1
              className="wf-display"
              style={{
                fontSize: 26,
                fontWeight: 700,
                letterSpacing: "-0.02em",
                margin: 0,
              }}
            >
              Nova tarefa
            </h1>
            <div style={{ flex: 1 }} />
            {/* Busca à direita — filtra templates por título/descrição/categoria
                + sinônimos (ex: 'tráfego pago' acha 'Análise Meta/Google Ads'). */}
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                width: 280,
                maxWidth: "100%",
              }}
            >
              <Search
                size={14}
                strokeWidth={1.75}
                color="var(--wf-ink-3)"
                style={{
                  position: "absolute",
                  left: 12,
                  pointerEvents: "none",
                }}
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar template (ex: tráfego pago)"
                style={{
                  width: "100%",
                  padding: searchQuery ? "9px 32px 9px 34px" : "9px 12px 9px 34px",
                  fontSize: 13,
                  fontFamily: "var(--wf-font-body)",
                  border: "1px solid var(--wf-ink-5)",
                  borderRadius: 999,
                  background: "var(--wf-paper)",
                  color: "var(--wf-ink)",
                  outline: "none",
                  transition: "border-color 140ms ease",
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = "var(--nd-orange)";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = "var(--wf-ink-5)";
                }}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  title="Limpar"
                  style={{
                    position: "absolute",
                    right: 10,
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: "var(--wf-ink-5)",
                    border: 0,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--wf-ink)",
                  }}
                >
                  <X size={11} strokeWidth={2.5} />
                </button>
              )}
            </div>
          </div>
          <p style={{ fontSize: 13, color: "var(--wf-ink-2)", maxWidth: 560 }}>
            Escolha o tipo de entrega. Sua equipe cuida do resto, e cada template já vem
            calibrado pra usar o briefing da sua empresa.
          </p>
        </div>
      </header>

      {/* Body */}
      <div style={{ padding: "28px 32px 48px", maxWidth: 1280, margin: "0 auto" }}>
        {PHASE_ORDER.filter((phase) => grouped[phase.key]?.length).map((phase) => {
          const items = grouped[phase.key]!;
          const readyCount = items.filter((t) => !isBlockedTemplate(t)).length;
          const soonCount = items.length - readyCount;
          const phaseColor = PHASE_COLORS[phase.key]!;
          const PhaseIcon = phase.icon;
          return (
            <section key={phase.key} style={{ marginBottom: 40 }}>
              {/* Phase header — número grande + ícone + label + meta + agentes */}
              <header
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 16,
                  marginBottom: 18,
                  paddingBottom: 14,
                  borderBottom: `2px solid ${phaseColor.primary}`,
                }}
              >
                <div
                  style={{
                    fontSize: 36,
                    fontWeight: 800,
                    letterSpacing: "-0.04em",
                    color: phaseColor.primary,
                    fontFamily: "var(--wf-font-display)",
                    lineHeight: 1,
                    marginTop: 2,
                  }}
                >
                  {phase.number}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 4,
                    }}
                  >
                    <span
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 6,
                        background: phaseColor.soft,
                        color: phaseColor.primary,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <PhaseIcon size={14} strokeWidth={2} />
                    </span>
                    <h2
                      className="wf-display"
                      style={{
                        fontSize: 19,
                        fontWeight: 700,
                        letterSpacing: "-0.02em",
                        color: "var(--wf-ink)",
                        margin: 0,
                      }}
                    >
                      {phase.label}
                    </h2>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: "var(--wf-ink-3)",
                        marginLeft: 4,
                      }}
                    >
                      {readyCount} disponíveis
                      {soonCount > 0 && ` · ${soonCount} em breve`}
                    </span>
                  </div>
                  <p
                    style={{
                      fontSize: 12.5,
                      color: "var(--wf-ink-2)",
                      margin: 0,
                      marginBottom: 3,
                      lineHeight: 1.5,
                    }}
                  >
                    {phase.description}
                  </p>
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--wf-ink-3)",
                      fontStyle: "italic",
                      margin: 0,
                    }}
                  >
                    {phase.agents}
                  </p>
                </div>
              </header>
              {(() => {
                const ready = items.filter((t) => !isBlockedTemplate(t));
                const blocked = items.filter((t) => isBlockedTemplate(t));
                const isExpanded = expandedPhases.has(phase.key);
                const visibleBlocked = isExpanded
                  ? blocked
                  : blocked.slice(0, COMING_SOON_VISIBLE_LIMIT);
                const hiddenCount = blocked.length - visibleBlocked.length;
                const visibleItems = [...ready, ...visibleBlocked];
                return (
                  <>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                        gap: 14,
                      }}
                    >
                      {visibleItems.map((template, i) => (
                        <TemplateCard
                          key={template.id}
                          template={template}
                          onClick={() => onSelect(template)}
                          delay={i * 0.03}
                          lockedIntegration={resolveLock(template)}
                          phaseColor={phaseColor}
                        />
                      ))}
                    </div>
                    {(hiddenCount > 0 || isExpanded) && blocked.length > COMING_SOON_VISIBLE_LIMIT && (
                      <button
                        type="button"
                        onClick={() => togglePhaseExpanded(phase.key)}
                        style={{
                          marginTop: 12,
                          padding: "8px 14px",
                          background: "transparent",
                          border: `1px dashed ${phaseColor.primary}`,
                          color: phaseColor.primary,
                          borderRadius: 999,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                          fontFamily: "inherit",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        {isExpanded
                          ? "Mostrar menos"
                          : `Mostrar mais ${hiddenCount} em breve`}
                      </button>
                    )}
                  </>
                );
              })()}
            </section>
          );
        })}
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────
// Step 2 — Configuration
// ─────────────────────────────────────────────────────────────

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: TemplateField;
  value: string;
  onChange: (v: string) => void;
}) {
  const baseStyle: React.CSSProperties = {
    background: "var(--wf-paper)",
    border: "1px solid var(--wf-ink-5)",
    borderRadius: 8,
    padding: "9px 12px",
    fontSize: 14,
    fontFamily: "var(--wf-font-body)",
    color: "var(--wf-ink)",
    width: "100%",
    outline: "none",
  };

  if (field.type === "textarea") {
    return (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        rows={4}
        style={{ ...baseStyle, minHeight: 100, resize: "vertical", lineHeight: 1.5 }}
      />
    );
  }
  if (field.type === "select" && field.options) {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} style={baseStyle}>
        <option value="">— escolha —</option>
        {field.options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      type={field.type === "url" ? "url" : "text"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.placeholder}
      style={baseStyle}
    />
  );
}

// ─────────────────────────────────────────────────────────────
// KB picker
// ─────────────────────────────────────────────────────────────

const KB_TYPE_META: Record<
  Exclude<KnowledgeBaseType, "onboarding">,
  { label: string; plural: string; icon: typeof BookOpen; color: string }
> = {
  product: { label: "Produto", plural: "Produtos", icon: Package, color: "var(--nd-orange)" },
  material: { label: "Material", plural: "Materiais", icon: FileText, color: "#0e7490" },
  competitor: { label: "Concorrente", plural: "Concorrentes", icon: Target, color: "#d92d4a" },
  reference: { label: "Referência", plural: "Referências", icon: Lightbulb, color: "#8b5cf6" },
  custom: { label: "Custom", plural: "Custom", icon: Sparkles, color: "#3aa657" },
};

/** Ordem das seções no picker — espelha a página de KBs (Briefing). */
const KB_TYPE_ORDER = [
  "product",
  "material",
  "competitor",
  "reference",
  "custom",
] as const;

/** Subtítulo de seção do picker (cor do tipo + contagem), full-width na grade. */
function KbSectionHeader({
  label,
  color,
  count,
  first,
}: {
  label: string;
  color: string;
  count: number;
  first: boolean;
}) {
  return (
    <div
      style={{
        gridColumn: "1 / -1",
        display: "flex",
        alignItems: "center",
        gap: 7,
        marginTop: first ? 0 : 10,
        marginBottom: 1,
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: color,
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color,
        }}
      >
        {label}
      </span>
      <span style={{ fontSize: 11, color: "var(--wf-ink-3)" }}>{count}</span>
    </div>
  );
}

/** Card de uma KB no picker — herda a cor do tipo (borda/ícone quando ativo). */
function KbPickerItem({
  kb,
  meta,
  selected,
  onToggle,
}: {
  kb: KnowledgeBase;
  meta: { icon: typeof BookOpen; color: string };
  selected: boolean;
  onToggle: (id: string) => void;
}) {
  const Icon = meta.icon;
  return (
    <button
      type="button"
      onClick={() => onToggle(kb.id)}
      style={{
        cursor: "pointer",
        padding: "10px 12px",
        background: selected
          ? `color-mix(in srgb, ${meta.color} 8%, var(--wf-paper))`
          : "var(--wf-paper)",
        border: selected
          ? `1px solid ${meta.color}`
          : "1px solid var(--wf-ink-5)",
        borderRadius: 10,
        textAlign: "left",
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        fontFamily: "var(--wf-font-body)",
      }}
    >
      <span
        style={{
          width: 26,
          height: 26,
          borderRadius: 6,
          background: selected
            ? meta.color
            : `color-mix(in srgb, ${meta.color} 14%, transparent)`,
          color: selected ? "#fff" : meta.color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          marginTop: 1,
        }}
      >
        {selected ? (
          <Check size={13} strokeWidth={2.5} />
        ) : (
          <Icon size={13} strokeWidth={1.75} />
        )}
      </span>
      <span style={{ minWidth: 0, flex: 1 }}>
        <span
          style={{
            display: "block",
            fontSize: 13,
            fontWeight: 600,
            color: "var(--wf-ink)",
            lineHeight: 1.3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {kb.name}
        </span>
        {kb.description && (
          <span
            style={{
              fontSize: 11,
              color: "var(--wf-ink-3)",
              marginTop: 2,
              lineHeight: 1.4,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {kb.description}
          </span>
        )}
      </span>
    </button>
  );
}

function KbPicker({
  kbs,
  selectedIds,
  onToggle,
  isLoading,
}: {
  kbs: KnowledgeBase[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  isLoading: boolean;
}) {
  const navigate = useNavigate();
  if (isLoading) return null;
  // Mostra mesmo sem nenhuma KB modular — o card "Nova base" no fim sempre
  // aparece pro user adicionar contexto do meio da criação da tarefa.

  // Agrupa por tipo (espelha a página de KBs): cada tipo vira uma seção com
  // subtítulo colorido e os cards herdam a cor do tipo. Numa conta com muitas
  // KBs fica fácil achar a base certa pra aquela tarefa.
  const groups = KB_TYPE_ORDER.map((type) => ({
    type,
    meta: KB_TYPE_META[type],
    items: kbs.filter((k) => k.type === type),
  })).filter((g) => g.items.length > 0);
  const knownTypes = new Set<string>(KB_TYPE_ORDER);
  const others = kbs.filter((k) => !knownTypes.has(k.type));

  const items: ReactElement[] = [];
  groups.forEach((g, gi) => {
    items.push(
      <KbSectionHeader
        key={`h-${g.type}`}
        label={g.meta.plural}
        color={g.meta.color}
        count={g.items.length}
        first={gi === 0}
      />,
    );
    g.items.forEach((kb) =>
      items.push(
        <KbPickerItem
          key={kb.id}
          kb={kb}
          meta={g.meta}
          selected={selectedIds.includes(kb.id)}
          onToggle={onToggle}
        />,
      ),
    );
  });
  if (others.length > 0) {
    items.push(
      <KbSectionHeader
        key="h-outras"
        label="Outras"
        color="var(--wf-ink-3)"
        count={others.length}
        first={groups.length === 0}
      />,
    );
    others.forEach((kb) =>
      items.push(
        <KbPickerItem
          key={kb.id}
          kb={kb}
          meta={{ icon: Sparkles, color: "var(--wf-ink-3)" }}
          selected={selectedIds.includes(kb.id)}
          onToggle={onToggle}
        />,
      ),
    );
  }

  return (
    <div>
      <label
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: "var(--wf-ink)",
          letterSpacing: "-0.005em",
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 4,
        }}
      >
        <BookOpen size={13} strokeWidth={1.75} color="var(--nd-orange)" />
        Bases de conhecimento adicionais
      </label>
      <p
        style={{
          fontSize: 11,
          color: "var(--wf-ink-3)",
          lineHeight: 1.5,
          marginBottom: 10,
        }}
      >
        Marque as bases que essa tarefa precisa. Onboarding já vai sempre. Quanto mais
        contexto relevante, melhor o entregável.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
          gap: 8,
        }}
      >
        {items}
        {/* Card "Nova base" no fim — mesmo padrão da página de KBs. Click
            leva pro wizard de criação. Permite adicionar contexto sem sair
            do fluxo de criar tarefa. */}
        <button
          type="button"
          onClick={() => navigate("/briefing/nova")}
          className="wf-card"
          style={{
            cursor: "pointer",
            padding: "10px 12px",
            background: "transparent",
            border: "1.5px dashed var(--wf-ink-4)",
            boxShadow: "none",
            borderRadius: 10,
            display: "flex",
            gap: 10,
            alignItems: "center",
            color: "var(--wf-ink-2)",
            transition: "border-color 150ms ease, color 150ms ease",
            minHeight: 50,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "var(--nd-orange)";
            e.currentTarget.style.color = "var(--nd-orange-pun)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "var(--wf-ink-4)";
            e.currentTarget.style.color = "var(--wf-ink-2)";
          }}
        >
          <span
            style={{
              width: 26,
              height: 26,
              borderRadius: 8,
              background: "rgba(255,112,0,0.08)",
              color: "var(--nd-orange)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              marginTop: 1,
            }}
          >
            <Plus size={14} strokeWidth={2} />
          </span>
          <span style={{ minWidth: 0, flex: 1 }}>
            <span
              style={{
                display: "block",
                fontSize: 13,
                fontWeight: 600,
                color: "var(--wf-ink)",
                lineHeight: 1.3,
              }}
            >
              Nova base
            </span>
            <span
              style={{
                fontSize: 11,
                color: "var(--wf-ink-3)",
                marginTop: 2,
                lineHeight: 1.4,
                display: "block",
              }}
            >
              Adiciona produto, material, concorrente ou persona
            </span>
          </span>
        </button>
      </div>
    </div>
  );
}

function ConfigStep({
  template,
  companyId,
  onBack,
}: {
  template: IssueTemplate;
  companyId: string;
  onBack: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [selectedKbIds, setSelectedKbIds] = useState<string[]>([]);
  /** Orientação livre que o user dá pra essa tarefa específica — vai pro topo
   *  da description e é injetada no prompt na execução. Aparece como primeiro
   *  campo do form, antes dos fields do template. */
  const [userOrientation, setUserOrientation] = useState("");
  /** Briefing de arte (só pra templates que geram arte). */
  const [artBriefing, setArtBriefing] = useState<ArtBriefing>({});
  /** Espaço (organização do kanban) — opcional. Pré-seleciona o espaço que o
   *  user estava filtrando no Quadro (continuidade natural do fluxo). */
  const [espacoId, setEspacoId] = useState<string>(() => {
    const saved = localStorage.getItem(`quadro-espaco:${companyId}`);
    return saved && saved !== "todos" && saved !== "geral" ? saved : "";
  });
  const espacosQuery = useQuery({
    queryKey: ["companies", companyId, "projects"],
    queryFn: () => projectsApi.list(companyId),
    staleTime: 30_000,
  });
  const espacos = useMemo(
    () => (espacosQuery.data ?? []).filter((p) => !p.archivedAt),
    [espacosQuery.data],
  );
  // Produz arte quando encadeia num template *_arte (ex: post_instagram →
  // post_instagram_arte) ou é ele mesmo de arte.
  const producesArt =
    !!template.nextTemplateKey?.endsWith("_arte") || template.key.endsWith("_arte");
  // Atalho "já tenho a copy": só nos templates de COPY que encadeiam arte.
  const canPasteCopy = !!template.nextTemplateKey?.endsWith("_arte");
  const [pastedCopy, setPastedCopy] = useState("");

  // Variações de título (Fase 3): imagem base (biblioteca/upload) + headlines
  // digitadas OU geradas pela IA. Reusa o pastedCopy como "headlines do user".
  const isTitleVariations = template.key === "variacoes_titulo";
  const [baseImageAssetId, setBaseImageAssetId] = useState<string | null>(null);
  const [baseImagePreview, setBaseImagePreview] = useState<string | null>(null);
  // Imagens de referência one-off desta tarefa (foto do sujeito + estilo),
  // escolhidas na criação. Viram executionState.selectedReferenceAssetIds.
  const [refAssetIds, setRefAssetIds] = useState<string[]>([]);
  const [basePickerOpen, setBasePickerOpen] = useState(false);
  const [noCopyMode, setNoCopyMode] = useState(false);
  const [variationsCount, setVariationsCount] = useState("3");
  const [uploadingBase, setUploadingBase] = useState(false);

  const knowledgeQuery = useQuery({
    queryKey: queryKeys.companies.knowledge(companyId),
    queryFn: () => knowledgeApi.list(companyId),
    staleTime: 60_000,
  });

  const modularKbs = useMemo(
    () => (knowledgeQuery.data ?? []).filter((kb) => kb.type !== "onboarding"),
    [knowledgeQuery.data],
  );

  const toggleKb = (id: string) =>
    setSelectedKbIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  // ProductId da tarefa = primeiro KB type=product entre os selecionados.
  // Não tem dropdown separado — selecionar o KB de produto na lista já
  // serve pra (a) injetar contexto do produto na Camada 2b E (b) vincular
  // issue.product_id pra brand library funcionar quando gera arte.
  const derivedProductId = useMemo(() => {
    const kbs = knowledgeQuery.data ?? [];
    const productKb = selectedKbIds
      .map((id) => kbs.find((kb) => kb.id === id))
      .find((kb) => kb?.type === "product");
    return productKb?.id ?? null;
  }, [selectedKbIds, knowledgeQuery.data]);

  // Biblioteca de aprovados do produto — pra escolher a imagem base (variações
  // de título). Só carrega quando o template é variacoes_titulo e há produto.
  const baseLibraryQuery = useQuery({
    queryKey: ["product-library", companyId, derivedProductId],
    queryFn: () => productLibraryApi.get(companyId, derivedProductId!),
    enabled: isTitleVariations && !!derivedProductId,
    staleTime: 30_000,
  });
  const handleBaseUpload = async (file: File) => {
    setUploadingBase(true);
    try {
      const res = await assetsApi.uploadImage(companyId, file);
      setBaseImageAssetId(res.assetId);
      setBaseImagePreview(`/api/assets/${res.assetId}/content`);
    } catch (e) {
      alert(`Não consegui subir a imagem: ${(e as Error).message}`);
    } finally {
      setUploadingBase(false);
    }
  };

  /**
   * Smart title determinístico: pega o primeiro field "tema/assunto/objetivo"
   * preenchido e monta `${categoria}: ${valor}`. Sem isso, todos os cards do
   * Kanban saem com título genérico ("Carrossel · copy") e ficam impossível
   * de distinguir. Roda 100% client-side, custo zero.
   *
   * Heurística:
   *  - Procura field whose key matches /tema|assunto|objetivo|topic|titulo/i
   *  - Se valor existe e tem 2-80 chars, usa como sufixo
   *  - Categoria = parte antes do "·" do template.title (ex: "Carrossel · copy" → "Carrossel")
   *  - Capitaliza primeira letra, mantém o resto
   */
  const buildSmartTitle = (): string | null => {
    const themeFieldKeys = /^(tema|assunto|objetivo|topic|titulo|produto)$/i;
    const themeField = template.fields.find((f) => themeFieldKeys.test(f.key));
    const rawValue = themeField ? fieldValues[themeField.key]?.trim() : "";
    if (!rawValue || rawValue.length < 2 || rawValue.length > 80) return null;
    const category = template.title.split("·")[0]?.trim() ?? template.title;
    const cleanValue = rawValue.charAt(0).toUpperCase() + rawValue.slice(1);
    return `${category}: ${cleanValue}`;
  };

  const createIssue = useMutation({
    mutationFn: () =>
      templatesApi.createIssue(companyId, {
        templateKey: template.key,
        // Modo "gerar": injeta quantas_variacoes pro prompt produzir N headlines.
        fieldValues:
          isTitleVariations && noCopyMode
            ? { ...fieldValues, quantas_variacoes: variationsCount }
            : fieldValues,
        customTitle: buildSmartTitle(),
        productId: derivedProductId,
        knowledgeBaseIds: selectedKbIds,
        userOrientation: userOrientation.trim() || null,
        artBriefing: producesArt && !isTitleVariations ? artBriefing : null,
        // Variações de título: headlines digitadas (a não ser que seja modo gerar).
        pastedCopy: isTitleVariations
          ? noCopyMode
            ? null
            : pastedCopy.trim() || null
          : canPasteCopy && pastedCopy.trim()
            ? pastedCopy
            : null,
        baseImageAssetId: isTitleVariations ? baseImageAssetId : null,
        // Imagens de referência one-off (foto do sujeito + estilo) — só pras
        // tarefas que geram arte (não pras variações de título).
        selectedReferenceAssetIds:
          producesArt && !isTitleVariations && refAssetIds.length > 0
            ? refAssetIds
            : null,
        // Espaço escolhido (a subtask de arte herda sozinha no encadeamento).
        projectId: espacoId && espacos.some((e) => e.id === espacoId) ? espacoId : null,
      }),
    onSuccess: ({ issue }) => {
      queryClient.invalidateQueries({ queryKey: ["companies", companyId, "issues"] });
      queryClient.invalidateQueries({ queryKey: ["companies", companyId, "dashboard"] });
      navigate(`/execucao/${issue.id}`);
    },
  });

  const missingRequiredLabels = template.fields
    .filter((f) => f.required && !fieldValues[f.key]?.trim())
    .map((f) => f.label);
  // Arte/criativo SEM nenhum campo obrigatório (ex: criativo estático): exige
  // PELO MENOS uma orientação (ou algo no briefing de arte) — senão a pessoa
  // cria a tarefa e a IA fica sem nenhum insumo pra trabalhar.
  const artBriefingEmpty = Object.values(artBriefing).every((v) => !(v ?? "").trim());
  const artNeedsBrief =
    producesArt &&
    !isTitleVariations &&
    !template.fields.some((f) => f.required) &&
    !userOrientation.trim() &&
    artBriefingEmpty;
  if (artNeedsBrief) missingRequiredLabels.push("Orientação pra IA (diga o que você quer)");
  // Variações de título: precisa da imagem base + (headlines digitadas OU gerar).
  if (isTitleVariations) {
    if (!baseImageAssetId) missingRequiredLabels.push("Imagem base");
    if (!noCopyMode && !pastedCopy.trim())
      missingRequiredLabels.push("Headlines (ou marque 'gerar com IA')");
  }
  const requiredMissing = missingRequiredLabels.length > 0;
  // Mostra o PORQUÊ não criou quando a pessoa clica sem preencher as obrigatórias.
  const [showRequiredHint, setShowRequiredHint] = useState(false);

  const meta = getTemplateMeta(template.key);
  const Icon = meta?.icon ?? Sparkles;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
    >
      {/* Hero */}
      <div
        style={{
          background:
            "linear-gradient(120deg, #050810 0%, #0d0f1f 50%, #1a1424 100%)",
          color: "#fff",
          padding: "32px 32px 28px",
          borderBottom: "1px solid var(--wf-ink-5)",
        }}
      >
        <div style={{ maxWidth: 760, margin: "0 auto" }}>
          <button
            type="button"
            onClick={onBack}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              fontSize: 12,
              padding: "4px 12px 4px 8px",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: 999,
              background: "rgba(255,255,255,0.03)",
              color: "rgba(255,255,255,0.75)",
              cursor: "pointer",
              fontFamily: "var(--wf-font-body)",
              marginBottom: 20,
              fontWeight: 500,
            }}
          >
            <ArrowLeft size={12} strokeWidth={2} /> Voltar
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: "rgba(255,112,0,0.18)",
                color: "var(--nd-orange)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Icon size={20} strokeWidth={1.75} />
            </div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.55)",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  marginBottom: 2,
                }}
              >
                {template.category}
                {meta?.eta && <span style={{ marginLeft: 8 }}>· {meta.eta}</span>}
              </div>
              <h1
                className="wf-display"
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1,
                  margin: 0,
                }}
              >
                {template.title}
              </h1>
            </div>
          </div>
          {meta?.userDescription && (
            <p
              style={{
                fontSize: 13,
                color: "rgba(255,255,255,0.7)",
                marginTop: 14,
                maxWidth: 560,
                lineHeight: 1.55,
              }}
            >
              {meta.userDescription}
            </p>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: "28px 32px 48px" }}>
        <div
          style={{
            maxWidth: 760,
            margin: "0 auto",
            display: "flex",
            flexDirection: "column",
            gap: 22,
          }}
        >
          {/* KB picker — selecionar um KB tipo "Produto" aqui vincula a
              tarefa ao produto (brand library, personas, tom). Não tem
              dropdown separado, o KB de produto na lista já faz isso. */}
          <KbPicker
            kbs={modularKbs}
            selectedIds={selectedKbIds}
            onToggle={toggleKb}
            isLoading={knowledgeQuery.isLoading}
          />

          {/* Campo universal de orientação — primeiro de todos, mesma
              estilização dos fields do template. Texto curto e livre que vai
              pro topo da description da issue e é considerado no prompt. */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--wf-ink)",
                  letterSpacing: "-0.005em",
                }}
              >
                Como você quer essa tarefa?
                {artNeedsBrief && <RequiredMark />}
              </span>
              <span style={{ fontSize: 12, color: "var(--wf-ink-3)", fontWeight: 500, marginTop: -1 }}>
                Direcione como você quer que seja feito, igual como você falaria pro ChatGPT.
              </span>
              <textarea
                value={userOrientation}
                onChange={(e) => setUserOrientation(e.target.value)}
                placeholder="Ex: quero algo bem direto, focado em conversão. Sem rodeio. Lembrar que estou no início do mês, então quero gerar autoridade primeiro."
                rows={3}
                maxLength={8000}
                style={{
                  background: "var(--wf-paper)",
                  border: "1px solid var(--wf-ink-5)",
                  borderRadius: 8,
                  padding: "10px 12px",
                  fontSize: 14,
                  fontFamily: "var(--wf-font-body)",
                  color: "var(--wf-ink)",
                  outline: "none",
                  resize: "vertical",
                  lineHeight: 1.5,
                  transition: "border-color 120ms ease, box-shadow 120ms ease",
                }}
              />
            </label>
          </div>

          {/* Espaço — só aparece se o user criou espaços no Quadro. Opcional. */}
          {espacos.length > 0 && (
            <label style={{ display: "flex", flexDirection: "column", gap: 5, maxWidth: 320 }}>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--wf-ink)",
                  letterSpacing: "-0.005em",
                }}
              >
                Espaço
              </span>
              <span style={{ fontSize: 12, color: "var(--wf-ink-3)", fontWeight: 500, marginTop: -1 }}>
                Em qual espaço do quadro essa tarefa entra (organização, opcional).
              </span>
              <select
                value={espacoId}
                onChange={(e) => setEspacoId(e.target.value)}
                style={{
                  background: "var(--wf-paper)",
                  border: "1px solid var(--wf-ink-5)",
                  borderRadius: 10,
                  padding: "9px 12px",
                  fontSize: 13,
                  fontFamily: "var(--wf-font-body)",
                  color: "var(--wf-ink)",
                  outline: "none",
                }}
              >
                <option value="">Geral (sem espaço)</option>
                {espacos.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {/* Briefing de arte — só pra tarefas que geram imagem (não pras
              variações de título, que partem de uma imagem que já existe) */}
          {producesArt && !isTitleVariations && (
            <ArtBriefingQuestions
              value={artBriefing}
              onChange={setArtBriefing}
              templateKey={template.key}
              framed
            />
          )}

          {/* Imagens de referência da tarefa (foto do sujeito + estilo) — one-off,
              valem só pra esta arte. Não aparece pras variações de título (que já
              partem de uma imagem base própria). */}
          {producesArt && !isTitleVariations && (
            <TaskArtImageInputs
              companyId={companyId}
              value={refAssetIds}
              onChange={setRefAssetIds}
            />
          )}

          {/* Variações de título: imagem base + headlines (digita ou gera) */}
          {isTitleVariations && (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {/* Imagem base */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--wf-ink)" }}>
                  Imagem base <RequiredMark />
                </span>
                <span style={{ fontSize: 11, color: "var(--wf-ink-3)", marginTop: -4, lineHeight: 1.5 }}>
                  A arte que você já tem e quer testar com outros títulos. Cada
                  headline é aplicada nela, preservando o resto.
                </span>
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                  {baseImagePreview ? (
                    <div style={{ position: "relative" }}>
                      <img
                        src={baseImagePreview}
                        alt="imagem base"
                        style={{ width: 120, height: 150, objectFit: "cover", borderRadius: 10, border: "1px solid var(--wf-ink-5)" }}
                      />
                      <button
                        type="button"
                        onClick={() => { setBaseImageAssetId(null); setBaseImagePreview(null); }}
                        title="Remover"
                        style={{ position: "absolute", top: -8, right: -8, width: 24, height: 24, borderRadius: "50%", border: "none", background: "var(--nd-orange)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        <X size={13} />
                      </button>
                    </div>
                  ) : (
                    <div style={{ width: 120, height: 150, borderRadius: 10, border: "1.5px dashed var(--wf-ink-5)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--wf-ink-3)", fontSize: 11, textAlign: "center", padding: 8 }}>
                      nenhuma imagem
                    </div>
                  )}
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => setBasePickerOpen(true)}
                      disabled={!derivedProductId || !(baseLibraryQuery.data?.creativeLibrary?.length)}
                      className="wf-btn wf-btn--ghost wf-btn--sm"
                      style={{ opacity: !derivedProductId || !(baseLibraryQuery.data?.creativeLibrary?.length) ? 0.5 : 1 }}
                      title={!derivedProductId ? "Selecione o produto pra usar a biblioteca de aprovados" : undefined}
                    >
                      Da biblioteca de aprovados
                    </button>
                    <label className="wf-btn wf-btn--ghost wf-btn--sm" style={{ cursor: "pointer", textAlign: "center" }}>
                      {uploadingBase ? "enviando…" : "Subir do computador"}
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        disabled={uploadingBase}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) void handleBaseUpload(f);
                          e.target.value = "";
                        }}
                      />
                    </label>
                  </div>
                </div>
              </div>

              {/* Headlines: digita ou gera */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: "var(--wf-ink)" }}>
                  <input
                    type="checkbox"
                    checked={noCopyMode}
                    onChange={(e) => setNoCopyMode(e.target.checked)}
                  />
                  Não tenho os títulos (gerar variações com IA)
                </label>
                {noCopyMode ? (
                  <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "var(--wf-ink)" }}>
                      Quantas variações gerar
                    </span>
                    <select
                      value={variationsCount}
                      onChange={(e) => setVariationsCount(e.target.value)}
                      style={{ background: "var(--wf-paper)", border: "1px solid var(--wf-ink-5)", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "var(--wf-ink)", maxWidth: 160 }}
                    >
                      {["1", "2", "3", "4", "5"].map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                    <span style={{ fontSize: 11, color: "var(--wf-ink-3)", lineHeight: 1.5 }}>
                      A IA cria os títulos, você revisa e escolhe quais viram arte.
                    </span>
                  </label>
                ) : (
                  <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "var(--wf-ink)" }}>
                      Seus títulos <RequiredMark />
                    </span>
                    <textarea
                      value={pastedCopy}
                      onChange={(e) => setPastedCopy(e.target.value)}
                      rows={4}
                      placeholder={"Um título por linha (até 5):\nPARE DE PERDER VENDAS\nSUA OFERTA MERECE MAIS"}
                      style={{ background: "var(--wf-paper)", border: "1px solid var(--wf-ink-5)", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "var(--wf-ink)", fontFamily: "var(--wf-font-body)", outline: "none", resize: "vertical", minHeight: 88 }}
                    />
                    <span style={{ fontSize: 11, color: "var(--wf-ink-3)", lineHeight: 1.5 }}>
                      Cada linha vira uma arte com aquele título. Vão direto pra geração.
                    </span>
                  </label>
                )}
              </div>
            </div>
          )}

          {/* Atalho "já tenho a copy" — pula a geração de copy e vai direto pra arte */}
          {canPasteCopy && !isTitleVariations && (
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "var(--wf-ink)" }}>
                Já tenho a copy{" "}
                <span style={{ fontWeight: 400, color: "var(--wf-ink-3)" }}>(opcional)</span>
              </span>
              <span style={{ fontSize: 11, color: "var(--wf-ink-3)", marginTop: -2, lineHeight: 1.5 }}>
                Cole sua copy pronta e a gente vai direto pra geração da arte. Deixe em branco pra
                gerar a copy normalmente.
              </span>
              <textarea
                value={pastedCopy}
                onChange={(e) => setPastedCopy(e.target.value)}
                rows={5}
                placeholder="Cole aqui a copy da peça (texto dos slides, hook, legenda...)"
                style={{
                  background: "var(--wf-paper)",
                  border: "1px solid var(--wf-ink-5)",
                  borderRadius: 8,
                  padding: "10px 12px",
                  fontSize: 14,
                  color: "var(--wf-ink)",
                  fontFamily: "var(--wf-font-body)",
                  outline: "none",
                  resize: "vertical",
                  minHeight: 96,
                }}
              />
            </label>
          )}

          {/* Template fields */}
          {template.fields.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {template.fields.map((field) => (
                <label
                  key={field.key}
                  style={{ display: "flex", flexDirection: "column", gap: 5 }}
                >
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "var(--wf-ink)",
                      letterSpacing: "-0.005em",
                    }}
                  >
                    {field.label}
                    {field.required && <RequiredMark />}
                  </span>
                  <FieldInput
                    field={field}
                    value={fieldValues[field.key] ?? ""}
                    onChange={(v) =>
                      setFieldValues((prev) => ({ ...prev, [field.key]: v }))
                    }
                  />
                </label>
              ))}
            </div>
          )}

          {createIssue.isError && (
            <Notice tone="danger" title="Não consegui criar a tarefa">
              {createIssue.error.message}
            </Notice>
          )}

          {showRequiredHint && requiredMissing && (
            <Notice tone="danger" title="Faltou preencher">
              Preencha {missingRequiredLabels.length === 1 ? "o campo obrigatório" : "os campos obrigatórios"}{" "}
              (marcados com <span style={{ color: "#e5484d", fontWeight: 700 }}>*</span>):{" "}
              {missingRequiredLabels.join(", ")}.
            </Notice>
          )}

          {/* Footer / actions */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginTop: 8,
              paddingTop: 18,
              borderTop: "1px solid var(--wf-ink-5)",
            }}
          >
            <button
              type="button"
              onClick={onBack}
              className="wf-btn wf-btn--ghost wf-btn--sm"
              disabled={createIssue.isPending}
            >
              Voltar
            </button>
            <div style={{ flex: 1 }} />
            <button
              type="button"
              onClick={() => {
                if (requiredMissing) {
                  setShowRequiredHint(true);
                  return;
                }
                setShowRequiredHint(false);
                createIssue.mutate();
              }}
              disabled={createIssue.isPending}
              className="wf-btn wf-btn--primary"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                opacity: createIssue.isPending ? 0.5 : 1,
                cursor: createIssue.isPending ? "not-allowed" : "pointer",
              }}
            >
              {createIssue.isPending ? "criando…" : "Criar tarefa"}
              {!createIssue.isPending && (
                <Check size={13} strokeWidth={2} color="#fff" />
              )}
            </button>
          </div>
        </div>
      </div>

      {basePickerOpen && baseLibraryQuery.data && (
        <ReferenceImagePickerModal
          library={baseLibraryQuery.data}
          companyId={companyId}
          isPending={false}
          onCancel={() => setBasePickerOpen(false)}
          onConfirm={(ids) => {
            const id = ids[0] ?? null;
            setBaseImageAssetId(id);
            setBaseImagePreview(id ? `/api/assets/${id}/content` : null);
            setBasePickerOpen(false);
          }}
        />
      )}
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────

export default function NovaTarefa() {
  const companyId = useCompanyId();
  const [searchParams, setSearchParams] = useSearchParams();
  const presetTemplateKey = searchParams.get("template");
  const [selectedTemplate, setSelectedTemplate] = useState<IssueTemplate | null>(null);
  // Modal de preview do fluxo — aparece entre o click no card e o form do
  // ConfigStep. Comunica visualmente o que vai acontecer no template antes
  // do user investir tempo preenchendo os campos.
  const [previewedTemplate, setPreviewedTemplate] = useState<IssueTemplate | null>(null);

  const templates = useQuery({
    queryKey: ["templates"],
    queryFn: () => templatesApi.list(),
  });

  const integrations = useQuery({
    queryKey: ["companies", companyId, "integrations"],
    queryFn: () => integrationsApi.list(companyId!),
    enabled: !!companyId,
    staleTime: 30_000,
  });

  // Pré-seleciona o template quando vem do "Comece por aqui" (?template=key).
  // Pula direto pro ConfigStep, eliminando o passo da galeria.
  useEffect(() => {
    if (!presetTemplateKey || selectedTemplate) return;
    const found = templates.data?.find((t) => t.key === presetTemplateKey);
    if (found) setSelectedTemplate(found);
  }, [presetTemplateKey, templates.data, selectedTemplate]);

  if (templates.isLoading) {
    return (
      <div style={{ padding: 32 }}>
        <span className="wf-hand-note">carregando templates…</span>
      </div>
    );
  }

  if (templates.isError) {
    return (
      <div style={{ padding: 32 }}>
        <div
          className="wf-card"
          style={{
            padding: 14,
            color: "var(--nd-orange-pun)",
            borderColor: "var(--nd-orange)",
          }}
        >
          erro ao carregar templates: {(templates.error as Error).message}
        </div>
      </div>
    );
  }

  if ((templates.data?.length ?? 0) === 0) {
    return (
      <div style={{ padding: 32, maxWidth: 720, margin: "0 auto" }}>
        <div className="wf-card wf-card--dashed" style={{ padding: 32, textAlign: "center" }}>
          <Sparkles size={28} strokeWidth={1.5} color="var(--nd-orange)" style={{ marginBottom: 10 }} />
          <div className="wf-hand-note" style={{ fontSize: 14 }}>
            nenhum template seedado ainda. rode <code>pnpm seed:nd:templates</code>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <AnimatePresence mode="wait">
        {selectedTemplate && companyId ? (
          <ConfigStep
            key="config"
            template={selectedTemplate}
            companyId={companyId}
            onBack={() => {
              // "Voltar" sempre leva pra tela de SELEÇÃO DE TEMPLATE (galeria).
              // Quando veio de um card, o template fica no ?template= da URL e o
              // efeito re-selecionava ele na hora (o "Voltar" parecia não fazer
              // nada) — então limpamos o param ANTES de zerar o template.
              if (presetTemplateKey) setSearchParams({}, { replace: true });
              setSelectedTemplate(null);
            }}
          />
        ) : (
          <GalleryStep
            key="gallery"
            templates={(templates.data ?? []).filter(isUserSelectableTemplate)}
            onSelect={setPreviewedTemplate}
            integrations={integrations.data ?? []}
          />
        )}
      </AnimatePresence>

      {/* Modal de preview do fluxo — comunica processo antes do form */}
      <AnimatePresence>
        {previewedTemplate && (
          <FlowPreviewModal
            template={previewedTemplate}
            onCancel={() => setPreviewedTemplate(null)}
            onConfirm={() => {
              setSelectedTemplate(previewedTemplate);
              setPreviewedTemplate(null);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}

/**
 * Modal lúdico explicativo do que vai acontecer ao executar um template.
 * Aparece entre o click no card e o form de configuração — não-técnico,
 * 2-4 passos visuais com ícones Lucide na cor da letra ESCALA.
 *
 * Resolve o problema apontado pelo user: confusão sobre se o template
 * só faz copy ou se inclui arte. O fluxo escrito remove ambiguidade.
 */
function FlowPreviewModal({
  template,
  onCancel,
  onConfirm,
}: {
  template: IssueTemplate;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const meta = getTemplateMeta(template.key);
  const Icon = meta?.icon ?? Sparkles;
  const flow = getTemplateFlow(template.key, !!template.nextTemplateKey, template.category);
  const phaseColor = PHASE_COLORS[template.phase] ?? PHASE_COLORS.C;
  const accent = phaseColor!.primary;
  const accentSoft = phaseColor!.soft;

  // Equipe envolvida — agentes distintos que tocam nessa tarefa, em ordem de
  // aparição no fluxo. "Você" (revisão) entra por último pra não competir com
  // os profissionais. Comunica visualmente que a tarefa passa por vários.
  const teamAgents = (() => {
    const seen = new Set<AgentKey>();
    const order: AgentKey[] = [];
    for (const step of flow) {
      if (step.agent && step.agent !== "voce" && !seen.has(step.agent)) {
        seen.add(step.agent);
        order.push(step.agent);
      }
    }
    return order;
  })();

  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(13,21,38,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <motion.div
        initial={{ y: 14, scale: 0.97, opacity: 0 }}
        animate={{ y: 0, scale: 1, opacity: 1 }}
        exit={{ y: 14, scale: 0.97, opacity: 0 }}
        transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
        style={{
          width: "min(720px, 100%)",
          background: "var(--wf-paper)",
          borderRadius: 16,
          border: "1px solid var(--wf-ink-5)",
          padding: 28,
          fontFamily: "var(--wf-font-body)",
          display: "flex",
          flexDirection: "column",
          gap: 20,
          maxHeight: "88vh",
          overflowY: "auto",
        }}
      >
        {/* Header — ícone + título + botão fechar */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: accentSoft,
              color: accent,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon size={20} strokeWidth={1.75} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2
              className="wf-display"
              style={{
                fontSize: 19,
                fontWeight: 700,
                letterSpacing: "-0.015em",
                margin: 0,
                color: "var(--wf-ink)",
              }}
            >
              {template.title}
            </h2>
            <p
              style={{
                fontSize: 12,
                color: "var(--wf-ink-3)",
                margin: 0,
                marginTop: 2,
                lineHeight: 1.4,
              }}
            >
              O que vai acontecer:
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Fechar"
            style={{
              background: "transparent",
              border: "none",
              padding: 6,
              borderRadius: 6,
              cursor: "pointer",
              color: "var(--wf-ink-3)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        {/* Equipe envolvida — chips dos profissionais que tocam essa tarefa.
            Comunica que uma tarefa passa por VÁRIOS agentes (não um só). */}
        {teamAgents.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                fontSize: 11,
                fontWeight: 700,
                color: "var(--wf-ink-3)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              Sua equipe nessa tarefa
              <InfoTooltip
                width={280}
                text="São especialistas de IA. Cada um lê o briefing e o contexto da sua empresa e cuida da parte dele (copy, design, dados, tráfego) pra entregar o conteúdo pronto. Quanto melhor o briefing, melhor o resultado."
              />
            </span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {teamAgents.map((key) => {
                const a = AGENTS[key];
                const AIcon = a.icon;
                return (
                  <span
                    key={key}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      padding: "4px 10px",
                      borderRadius: 999,
                      background: accentSoft,
                      color: accent,
                      fontSize: 11.5,
                      fontWeight: 600,
                      border: `1px solid ${accent}33`,
                    }}
                  >
                    <AIcon size={12} strokeWidth={2} /> {a.name}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Steps — fluxo HORIZONTAL. Cada passo: nó (ícone) sobre um trilho
            que passa POR TRÁS conectando os nós + label + "feito por [agente]".
            Scroll horizontal se não couber. */}
        <div style={{ overflowX: "auto", margin: "0 -4px", padding: "6px 4px 4px" }}>
          <div style={{ display: "flex", alignItems: "stretch", justifyContent: "center", minWidth: "min-content" }}>
            {flow.map((step, i) => {
              const StepIcon = step.icon;
              const stepAgent = step.agent ? AGENTS[step.agent] : null;
              const AgentIcon = stepAgent?.icon;
              const isFirst = i === 0;
              const isLast = i === flow.length - 1;
              const isReview = step.agent === "voce";
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.28, delay: 0.06 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  style={{
                    flex: "1 1 0",
                    minWidth: 134,
                    maxWidth: 210,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    gap: 9,
                    position: "relative",
                    padding: "0 4px",
                  }}
                >
                  {/* Trilho — segmentos esquerdo/direito no centro vertical do nó,
                      atrás dele (zIndex 0). Cria linha contínua passando pelos nós. */}
                  {!isFirst && (
                    <span style={{ position: "absolute", top: 25, left: 0, width: "50%", height: 2, background: accentSoft, zIndex: 0 }} />
                  )}
                  {!isLast && (
                    <span style={{ position: "absolute", top: 25, left: "50%", width: "50%", height: 2, background: accentSoft, zIndex: 0 }} />
                  )}
                  {/* Nó com halo branco (faz o trilho parecer entrar no nó) */}
                  <span
                    style={{
                      position: "relative",
                      zIndex: 1,
                      width: 52,
                      height: 52,
                      borderRadius: 14,
                      background: isReview ? "var(--wf-paper)" : accentSoft,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: `1.5px solid ${isReview ? "var(--wf-ink-4)" : accent}`,
                      boxShadow: "0 0 0 4px var(--wf-paper)",
                    }}
                  >
                    <StepIcon size={22} strokeWidth={1.9} color={isReview ? "var(--wf-ink-2)" : accent} />
                    <span
                      style={{
                        position: "absolute",
                        top: -7,
                        right: -7,
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        background: accent,
                        color: "#fff",
                        fontSize: 10,
                        fontWeight: 800,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontFamily: "var(--wf-font-display)",
                      }}
                    >
                      {i + 1}
                    </span>
                  </span>
                  {/* Label */}
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--wf-ink)", letterSpacing: "-0.01em", lineHeight: 1.25 }}>
                    {step.label}
                  </div>
                  {/* Desc */}
                  {step.desc && (
                    <div style={{ fontSize: 11, color: "var(--wf-ink-3)", lineHeight: 1.4 }}>{step.desc}</div>
                  )}
                  {/* Agente — "feito por X" (ou "Você" nos passos de revisão).
                      marginTop auto alinha os badges na base entre colunas. */}
                  {stepAgent && AgentIcon && (
                    <span
                      style={{
                        marginTop: "auto",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        padding: "3px 9px",
                        borderRadius: 999,
                        background: isReview ? "var(--wf-ink-6, #f3efe5)" : accentSoft,
                        color: isReview ? "var(--wf-ink-2)" : accent,
                        fontSize: 10.5,
                        fontWeight: 600,
                        lineHeight: 1.3,
                        border: `1px solid ${isReview ? "var(--wf-ink-5)" : accent + "33"}`,
                      }}
                    >
                      <AgentIcon size={11} strokeWidth={2} />
                      {isReview ? "Você" : stepAgent.name}
                    </span>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Footer — botões */}
        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          <button
            type="button"
            onClick={onCancel}
            className="wf-btn wf-btn--ghost"
            style={{ flex: 1 }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="wf-btn"
            style={{
              flex: 2,
              background: accent,
              color: "#fff",
              border: "none",
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            Quero fazer <ArrowRight size={14} strokeWidth={2.25} />
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
